import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSession } from '@gadgetinc/react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  Divider,
  Collapse,
  useDisclosure,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  IconButton,
  Tooltip,
  SimpleGrid,
  Switch,
  FormControl,
  FormLabel,
  Textarea,
  Select,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, DownloadIcon, RepeatIcon, StarIcon } from '@chakra-ui/icons';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { api } from '../api';

export default function ClinicalDocumentReader() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isDebugging, setIsDebugging] = useState(false);
  const [isEnhancedProcessing, setIsEnhancedProcessing] = useState(false);
  const [isOcrProcessing, setIsOcrProcessing] = useState(false);
  const [isLlmProcessing, setIsLlmProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [aiEvaluation, setAiEvaluation] = useState(null);
  const [debugResults, setDebugResults] = useState(null);
  const [ocrResults, setOcrResults] = useState(null);
  const [error, setError] = useState(null);
  const [trainingMode, setTrainingMode] = useState(false);
  const [trainingData, setTrainingData] = useState({});

  // Session debugging
  const session = useSession(api);

  // Collapsible sections
  const { isOpen: isDocumentInfoOpen, onToggle: toggleDocumentInfo } = useDisclosure({ defaultIsOpen: true });
  const { isOpen: isPatientsOpen, onToggle: togglePatients } = useDisclosure({ defaultIsOpen: true });
  const { isOpen: isAiEvaluationOpen, onToggle: toggleAiEvaluation } = useDisclosure({ defaultIsOpen: true });
  const { isOpen: isRawDataOpen, onToggle: toggleRawData } = useDisclosure();
  const { isOpen: isDebugOpen, onToggle: toggleDebug } = useDisclosure();
  const { isOpen: isOcrOpen, onToggle: toggleOcr } = useDisclosure();

  // Debug session on component mount
  useEffect(() => {
    if (session) {
      console.log('Session active:', {
        userId: session?.user?.id,
        email: session?.user?.email,
        authenticated: !!session
      });
    }
  }, [session]);

  // File drop handler
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please select a PDF file');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    multiple: false,
  });

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };

  // Process document - single step that extracts all data
  const processDocument = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setExtractedData(null);
    setAiEvaluation(null);

    try {
      console.log('Processing document:', selectedFile.name);
      
      // Convert file to base64
      const base64Data = await fileToBase64(selectedFile);
      
      const payload = {
        fileData: `data:${selectedFile.type};base64,${base64Data}`,
        filename: selectedFile.name,
        mimetype: selectedFile.type,
      };
      
      const response = await api.fetch('/process-clinical-docs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('Document processing result:', result);
      
      if (result.extracted_data) {
        setExtractedData(result.extracted_data);
        
        // Initialize training data for each patient
        if (result.extracted_data.patients) {
          const initialTrainingData = {};
          result.extracted_data.patients.forEach((patient, index) => {
            initialTrainingData[`patient_${index}`] = {
              rating: '',
              comments: '',
            };
          });
          setTrainingData(initialTrainingData);
        }
      } else {
        throw new Error('No extracted_data found in response');
      }
      
    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message || 'An error occurred while processing the document');
    } finally {
      setIsProcessing(false);
    }
  };

  // Process document with enhanced FISDAP parser
  const processDocumentEnhanced = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setIsEnhancedProcessing(true);
    setError(null);
    setExtractedData(null);
    setAiEvaluation(null);

    try {
      console.log('Enhanced processing document:', selectedFile.name);
      
      // Use FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('filename', selectedFile.name);
      
      const response = await api.fetch('/process-clinical-docs-enhanced', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('Enhanced document processing result:', result);
      
      if (result.extracted_data) {
        setExtractedData(result.extracted_data);
        
        // Initialize training data for each patient
        if (result.extracted_data.patients) {
          const initialTrainingData = {};
          result.extracted_data.patients.forEach((patient, index) => {
            initialTrainingData[`patient_${index}`] = {
              rating: '',
              comments: '',
            };
          });
          setTrainingData(initialTrainingData);
        }
      } else {
        throw new Error('No extracted_data found in response');
      }
      
    } catch (err) {
      console.error('Enhanced processing error:', err);
      setError(err.message || 'An error occurred while processing the document with enhanced parser');
    } finally {
      setIsEnhancedProcessing(false);
    }
  };

  // Process document with OCR
  const processDocumentOCR = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setIsOcrProcessing(true);
    setError(null);
    setExtractedData(null);
    setAiEvaluation(null);
    setOcrResults(null);

    try {
      console.log('OCR processing document:', selectedFile.name);
      
      // Convert file to base64
      const base64Data = await fileToBase64(selectedFile);
      
      const payload = {
        fileData: `data:${selectedFile.type};base64,${base64Data}`,
        filename: selectedFile.name,
        mimetype: selectedFile.type,
      };
      
      const response = await api.fetch('/process-clinical-docs-ocr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('OCR document processing result:', result);
      
      if (result.extracted_data) {
        setExtractedData(result.extracted_data);
        
        // Store OCR-specific results
        if (result.ocr_results) {
          setOcrResults(result.ocr_results);
        }
        
        // Initialize training data for each patient
        if (result.extracted_data.patients) {
          const initialTrainingData = {};
          result.extracted_data.patients.forEach((patient, index) => {
            initialTrainingData[`patient_${index}`] = {
              rating: '',
              comments: '',
            };
          });
          setTrainingData(initialTrainingData);
        }
      } else {
        throw new Error('No extracted_data found in response');
      }
      
    } catch (err) {
      console.error('OCR processing error:', err);
      setError(err.message || 'An error occurred while processing the document with OCR');
    } finally {
      setIsOcrProcessing(false);
    }
  };

  // Debug document processing
  const debugProcessDocument = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setIsDebugging(true);
    setError(null);
    setDebugResults(null);

    try {
      console.log('Debug processing document:', selectedFile.name);
      
      // Convert file to base64
      const base64Data = await fileToBase64(selectedFile);
      
      const payload = {
        fileData: `data:${selectedFile.type};base64,${base64Data}`,
        filename: selectedFile.name,
        mimetype: selectedFile.type,
      };
      
      const response = await api.fetch('/debug-pdf-processing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('Debug processing result:', result);
      setDebugResults(result);
      
    } catch (err) {
      console.error('Debug processing error:', err);
      setError(err.message || 'An error occurred while debug processing the document');
    } finally {
      setIsDebugging(false);
    }
  };

  // Process document with LLM multimodal
  const processDocumentLLM = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setIsLlmProcessing(true);
    setError(null);
    setExtractedData(null);
    setAiEvaluation(null);

    try {
      console.log('LLM multimodal processing document:', selectedFile.name);
      
      // Use FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('filename', selectedFile.name);
      
      const response = await api.fetch('/process-clinical-docs-llm', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('LLM multimodal processing result:', result);
      
      if (result.extracted_data) {
        setExtractedData(result.extracted_data);
        
        // Initialize training data for each patient
        if (result.extracted_data.patients) {
          const initialTrainingData = {};
          result.extracted_data.patients.forEach((patient, index) => {
            initialTrainingData[`patient_${index}`] = {
              rating: '',
              comments: '',
            };
          });
          setTrainingData(initialTrainingData);
        }
      } else {
        throw new Error('No extracted_data found in response');
      }
      
    } catch (err) {
      console.error('LLM multimodal processing error:', err);
      setError(err.message || 'An error occurred while processing the document with LLM multimodal');
    } finally {
      setIsLlmProcessing(false);
    }
  };

  // Evaluate with AI
  const evaluateWithAI = async () => {
    if (!extractedData) {
      setError('No document data to evaluate');
      return;
    }

    setIsEvaluating(true);
    setError(null);

    try {
      console.log('Evaluating with AI...');
      
      const payload = {
        extractedData,
        filename: selectedFile.name,
      };
      
      const response = await api.fetch('/evaluate-clinical-document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('AI evaluation result:', result);
      setAiEvaluation(result);
      
    } catch (err) {
      console.error('AI evaluation error:', err);
      setError(err.message || 'An error occurred during AI evaluation');
    } finally {
      setIsEvaluating(false);
    }
  };

  // Reset everything
  const reset = () => {
    setSelectedFile(null);
    setExtractedData(null);
    setAiEvaluation(null);
    setDebugResults(null);
    setOcrResults(null);
    setError(null);
    setTrainingMode(false);
    setTrainingData({});
    setIsEnhancedProcessing(false);
    setIsLlmProcessing(false);
  };

  // Handle training data changes
  const handleTrainingDataChange = (patientKey, field, value) => {
    setTrainingData(prev => ({
      ...prev,
      [patientKey]: {
        ...prev[patientKey],
        [field]: value,
      },
    }));
  };

  // Download JSON function
  const downloadJson = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box p={6} maxW="1200px" mx="auto">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center" wrap="wrap">
          <Heading size="lg" color="blue.600">
            Clinical Document Reader
          </Heading>
          {extractedData && (
            <Button 
              leftIcon={<RepeatIcon />} 
              size="sm" 
              variant="outline"
              onClick={reset}
            >
              Start Over
            </Button>
          )}
        </HStack>

        {/* Session Debug Info */}
        <Card size="sm" bg="gray.50">
          <CardBody>
            <VStack spacing={2} align="start">
              <Text fontSize="sm" fontWeight="bold" color="gray.600">Session Debug Info:</Text>
              <HStack spacing={4} fontSize="xs" color="gray.600">
                <Text>
                  Status: {session ? '✅ Authenticated' : '❌ Not Authenticated'}
                </Text>
                {session?.user?.email && (
                  <Text>User: {session.user.email}</Text>
                )}
                {session?.id && (
                  <Text>Session: {session.id.substring(0, 8)}...</Text>
                )}
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* File Upload Section */}
        {!extractedData && (
          <Card>
            <CardHeader>
              <Heading size="md">Upload Clinical Document</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                <Box
                  {...getRootProps()}
                  border="2px dashed"
                  borderColor={isDragActive ? "blue.300" : "gray.300"}
                  borderRadius="md"
                  p={8}
                  textAlign="center"
                  cursor="pointer"
                  bg={isDragActive ? "blue.50" : "gray.50"}
                  transition="all 0.2s"
                  _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                >
                  <input {...getInputProps()} />
                  {selectedFile ? (
                    <VStack spacing={2}>
                      <Text fontSize="lg" fontWeight="bold" color="green.600">
                        ✓ {selectedFile.name}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Text>
                    </VStack>
                  ) : (
                    <VStack spacing={2}>
                      <Text fontSize="lg">
                        {isDragActive
                          ? "Drop the PDF file here..."
                          : "Drag & drop a PDF file here, or click to select"}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Only PDF files are accepted
                      </Text>
                    </VStack>
                  )}
                </Box>

                <HStack spacing={4} wrap="wrap">
                  <Button
                    colorScheme="blue"
                    size="lg"
                    onClick={processDocument}
                    isLoading={isProcessing}
                    loadingText="Processing Document..."
                    isDisabled={!selectedFile || isProcessing || isDebugging || isEnhancedProcessing || isOcrProcessing || isLlmProcessing}
                  >
                    Process Document
                  </Button>
                  
                  <Button
                    colorScheme="green"
                    size="lg"
                    onClick={processDocumentEnhanced}
                    isLoading={isEnhancedProcessing}
                    loadingText="Enhanced Processing..."
                    isDisabled={!selectedFile || isProcessing || isDebugging || isEnhancedProcessing || isOcrProcessing || isLlmProcessing}
                  >
                    Enhanced FISDAP Parser
                  </Button>
                  
                  <Button
                    colorScheme="teal"
                    size="lg"
                    onClick={processDocumentOCR}
                    isLoading={isOcrProcessing}
                    loadingText="OCR Processing..."
                    isDisabled={!selectedFile || isProcessing || isDebugging || isEnhancedProcessing || isOcrProcessing || isLlmProcessing}
                  >
                    OCR Processing
                  </Button>
                  
                  <Button
                    colorScheme="purple"
                    size="lg"
                    onClick={processDocumentLLM}
                    isLoading={isLlmProcessing}
                    loadingText="LLM Processing..."
                    isDisabled={!selectedFile || isProcessing || isDebugging || isEnhancedProcessing || isOcrProcessing || isLlmProcessing}
                  >
                    🤖 LLM Multimodal
                  </Button>
                  
                  <Button
                    colorScheme="orange"
                    variant="outline"
                    size="lg"
                    onClick={debugProcessDocument}
                    isLoading={isDebugging}
                    loadingText="Debug Processing..."
                    isDisabled={!selectedFile || isProcessing || isDebugging || isEnhancedProcessing || isOcrProcessing || isLlmProcessing}
                  >
                    Debug Processing
                  </Button>
                </HStack>

                {error && (
                  <Alert status="error">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Debug Results Section */}
        {debugResults && (
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <HStack>
                  <Heading size="md">Debug Results</Heading>
                  <Badge colorScheme="orange">Debug Data</Badge>
                </HStack>
                <HStack>
                  <Tooltip label="Download Debug Results">
                    <IconButton
                      aria-label="Download debug results"
                      icon={<DownloadIcon />}
                      size="sm"
                      onClick={() => downloadJson(debugResults, `debug-results-${Date.now()}.json`)}
                    />
                  </Tooltip>
                  <IconButton
                    aria-label="Toggle debug results"
                    icon={isDebugOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    size="sm"
                    onClick={toggleDebug}
                  />
                </HStack>
              </HStack>
            </CardHeader>
            <Collapse in={isDebugOpen}>
              <CardBody pt={0}>
                <VStack align="start" spacing={4}>
                  {/* Text Extraction Info */}
                  {debugResults.textExtraction && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Text Extraction:</Text>
                      <VStack align="start" spacing={2}>
                        <Text fontSize="sm">
                          <strong>Characters:</strong> {debugResults.textExtraction.totalCharacters || 'N/A'}
                        </Text>
                        <Text fontSize="sm">
                          <strong>Pages:</strong> {debugResults.textExtraction.pages || 'N/A'}
                        </Text>
                        {debugResults.textExtraction.preview && (
                          <Box>
                            <Text fontSize="sm" fontWeight="semibold">Preview:</Text>
                            <Text fontSize="xs" color="gray.600" maxH="100px" overflowY="auto" p={2} bg="gray.50" borderRadius="md">
                              {debugResults.textExtraction.preview}
                            </Text>
                          </Box>
                        )}
                      </VStack>
                    </Box>
                  )}

                  {/* Patient Parsing Results */}
                  {debugResults.patientParsing && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Patient Parsing:</Text>
                      <VStack align="start" spacing={2}>
                        <Text fontSize="sm">
                          <strong>Patients Found:</strong> {debugResults.patientParsing.count || 0}
                        </Text>
                        {debugResults.patientParsing.patterns && (
                          <Box>
                            <Text fontSize="sm" fontWeight="semibold">Patterns Matched:</Text>
                            <VStack align="start" spacing={1}>
                              {debugResults.patientParsing.patterns.map((pattern, index) => (
                                <Text key={index} fontSize="xs" color="gray.600">• {pattern}</Text>
                              ))}
                            </VStack>
                          </Box>
                        )}
                      </VStack>
                    </Box>
                  )}

                  {/* Processing Steps */}
                  {debugResults.processingSteps && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Processing Steps:</Text>
                      <VStack align="start" spacing={1}>
                        {debugResults.processingSteps.map((step, index) => (
                          <Text key={index} fontSize="sm" color="gray.600">
                            {index + 1}. {step}
                          </Text>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {/* Raw Debug Data */}
                  <Box w="full">
                    <Text fontWeight="bold" mb={2}>Raw Debug Data:</Text>
                    <Box
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      overflow="auto"
                      maxH="400px"
                    >
                      <SyntaxHighlighter
                        language="json"
                        style={tomorrow}
                        customStyle={{
                          margin: 0,
                          fontSize: '12px',
                        }}
                      >
                        {JSON.stringify(debugResults, null, 2)}
                      </SyntaxHighlighter>
                    </Box>
                  </Box>
                </VStack>
              </CardBody>
            </Collapse>
          </Card>
        )}

        {/* OCR Results Section */}
        {ocrResults && (
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <HStack>
                  <Heading size="md">OCR Results</Heading>
                  <Badge colorScheme="teal">OCR Data</Badge>
                </HStack>
                <HStack>
                  <Tooltip label="Download OCR Results">
                    <IconButton
                      aria-label="Download OCR results"
                      icon={<DownloadIcon />}
                      size="sm"
                      onClick={() => downloadJson(ocrResults, `ocr-results-${Date.now()}.json`)}
                    />
                  </Tooltip>
                  <IconButton
                    aria-label="Toggle OCR results"
                    icon={isOcrOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    size="sm"
                    onClick={toggleOcr}
                  />
                </HStack>
              </HStack>
            </CardHeader>
            <Collapse in={isOcrOpen}>
              <CardBody pt={0}>
                <VStack align="start" spacing={4}>
                  {/* OCR Confidence Info */}
                  {ocrResults.confidence && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>OCR Confidence:</Text>
                      <VStack align="start" spacing={2}>
                        <Text fontSize="sm">
                          <strong>Overall Confidence:</strong> {(ocrResults.confidence * 100).toFixed(1)}%
                        </Text>
                        <Badge colorScheme={ocrResults.confidence >= 0.8 ? 'green' : ocrResults.confidence >= 0.6 ? 'yellow' : 'red'}>
                          {ocrResults.confidence >= 0.8 ? 'High Confidence' : ocrResults.confidence >= 0.6 ? 'Medium Confidence' : 'Low Confidence'}
                        </Badge>
                      </VStack>
                    </Box>
                  )}

                  {/* Extraction Method */}
                  {ocrResults.extraction_method && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Extraction Method:</Text>
                      <Text fontSize="sm" color="gray.600">
                        {ocrResults.extraction_method}
                      </Text>
                    </Box>
                  )}

                  {/* Processing Details */}
                  {ocrResults.processing_details && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Processing Details:</Text>
                      <VStack align="start" spacing={2}>
                        {ocrResults.processing_details.pages_processed && (
                          <Text fontSize="sm">
                            <strong>Pages Processed:</strong> {ocrResults.processing_details.pages_processed}
                          </Text>
                        )}
                        {ocrResults.processing_details.processing_time && (
                          <Text fontSize="sm">
                            <strong>Processing Time:</strong> {ocrResults.processing_details.processing_time}s
                          </Text>
                        )}
                        {ocrResults.processing_details.text_blocks && (
                          <Text fontSize="sm">
                            <strong>Text Blocks Found:</strong> {ocrResults.processing_details.text_blocks}
                          </Text>
                        )}
                      </VStack>
                    </Box>
                  )}

                  {/* OCR Text Preview */}
                  {ocrResults.text_preview && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>OCR Text Preview:</Text>
                      <Text fontSize="xs" color="gray.600" maxH="100px" overflowY="auto" p={2} bg="gray.50" borderRadius="md">
                        {ocrResults.text_preview}
                      </Text>
                    </Box>
                  )}

                  {/* Raw OCR Data */}
                  <Box w="full">
                    <Text fontWeight="bold" mb={2}>Raw OCR Data:</Text>
                    <Box
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      overflow="auto"
                      maxH="400px"
                    >
                      <SyntaxHighlighter
                        language="json"
                        style={tomorrow}
                        customStyle={{
                          margin: 0,
                          fontSize: '12px',
                        }}
                      >
                        {JSON.stringify(ocrResults, null, 2)}
                      </SyntaxHighlighter>
                    </Box>
                  </Box>
                </VStack>
              </CardBody>
            </Collapse>
          </Card>
        )}

        {/* Results Section */}
        {extractedData && (
          <>
            {/* Training Mode Toggle */}
            <Card>
              <CardBody>
                <HStack justify="space-between" align="center">
                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="training-mode" mb="0" fontSize="sm">
                      Training Mode
                    </FormLabel>
                    <Switch 
                      id="training-mode"
                      isChecked={trainingMode} 
                      onChange={(e) => setTrainingMode(e.target.checked)} 
                    />
                  </FormControl>
                  
                  <Button
                    colorScheme="purple"
                    leftIcon={<StarIcon />}
                    onClick={evaluateWithAI}
                    isLoading={isEvaluating}
                    loadingText="Evaluating..."
                    isDisabled={isEvaluating}
                  >
                    Evaluate with AI
                  </Button>
                </HStack>
              </CardBody>
            </Card>

            {/* Document Information */}
            <Card>
              <CardHeader>
                <HStack justify="space-between">
                  <HStack>
                    <Heading size="md">Document Information</Heading>
                    {extractedData.shift_locked !== null && (
                      <Badge colorScheme={extractedData.shift_locked ? 'green' : 'red'}>
                        {extractedData.shift_locked ? '🔒 Locked' : '🔓 Unlocked'}
                      </Badge>
                    )}
                  </HStack>
                  <IconButton
                    aria-label="Toggle document info"
                    icon={isDocumentInfoOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    size="sm"
                    onClick={toggleDocumentInfo}
                  />
                </HStack>
              </CardHeader>
              <Collapse in={isDocumentInfoOpen}>
                <CardBody pt={0}>
                  <VStack align="start" spacing={3}>
                    <Text><strong>File:</strong> {selectedFile.name}</Text>
                    <Text><strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</Text>
                    
                    {extractedData.document_info && (
                      <>
                        {extractedData.document_info.pages && (
                          <Text><strong>Pages:</strong> {extractedData.document_info.pages}</Text>
                        )}
                        {extractedData.document_info.date_range && (
                          <Text><strong>Date Range:</strong> {extractedData.document_info.date_range}</Text>
                        )}
                        {extractedData.document_info.location && (
                          <Text><strong>Location:</strong> {extractedData.document_info.location}</Text>
                        )}
                      </>
                    )}
                    
                    <HStack>
                      <Text><strong>Shift Lock:</strong></Text>
                      <Badge colorScheme={extractedData.shift_locked ? 'green' : 'red'}>
                        {extractedData.shift_locked ? 'Requirements Met' : 'Requirements Not Met'}
                      </Badge>
                    </HStack>
                  </VStack>
                </CardBody>
              </Collapse>
            </Card>

            {/* Patient Information */}
            <Card>
              <CardHeader>
                <HStack justify="space-between">
                  <HStack>
                    <Heading size="md">Patients ({extractedData.patients?.length || 0})</Heading>
                    {extractedData.patients?.length > 0 && (
                      <Badge colorScheme="blue">{extractedData.patients.length} found</Badge>
                    )}
                  </HStack>
                  <IconButton
                    aria-label="Toggle patients"
                    icon={isPatientsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    size="sm"
                    onClick={togglePatients}
                  />
                </HStack>
              </CardHeader>
              <Collapse in={isPatientsOpen}>
                <CardBody pt={0}>
                  {extractedData.patients && extractedData.patients.length > 0 ? (
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      {extractedData.patients.map((patient, index) => (
                        <Card key={index} variant="outline" size="sm">
                          <CardBody>
                            <VStack spacing={3} align="start">
                              {/* Patient Header */}
                              <Text fontWeight="bold" color="blue.600">
                                Patient {index + 1}
                              </Text>

                              {/* Patient Details */}
                              <VStack spacing={1} align="start" fontSize="sm">
                                {patient.age && (
                                  <Text><strong>Age:</strong> {patient.age}</Text>
                                )}
                                {patient.gender && (
                                  <Text><strong>Gender:</strong> {patient.gender}</Text>
                                )}
                                {patient.chief_complaint && (
                                  <Text><strong>Chief Complaint:</strong> {patient.chief_complaint}</Text>
                                )}
                                {patient.primary_impression && (
                                  <Text><strong>Primary:</strong> {patient.primary_impression}</Text>
                                )}
                                {patient.secondary_impression && (
                                  <Text><strong>Secondary:</strong> {patient.secondary_impression}</Text>
                                )}
                              </VStack>

                              {/* Checkbox Status */}
                              {extractedData.checkboxes && extractedData.checkboxes[`patient_${index}`] && (
                                <VStack spacing={2} align="start" fontSize="xs">
                                  <Text fontWeight="semibold">Validation Status:</Text>
                                  <HStack spacing={3}>
                                    <Text color={extractedData.checkboxes[`patient_${index}`].interview ? "green.500" : "gray.400"}>
                                      Interview: {extractedData.checkboxes[`patient_${index}`].interview ? "✓" : "✗"}
                                    </Text>
                                    <Text color={extractedData.checkboxes[`patient_${index}`].exam ? "green.500" : "gray.400"}>
                                      Exam: {extractedData.checkboxes[`patient_${index}`].exam ? "✓" : "✗"}
                                    </Text>
                                  </HStack>
                                </VStack>
                              )}

                              {/* Training Fields */}
                              {trainingMode && (
                                <VStack spacing={2} align="start" w="full">
                                  <Divider />
                                  <Text fontSize="xs" fontWeight="semibold" color="purple.600">
                                    Training Evaluation:
                                  </Text>
                                  <FormControl size="sm">
                                    <FormLabel fontSize="xs">Rating</FormLabel>
                                    <Select
                                      placeholder="Select rating"
                                      size="sm"
                                      value={trainingData[`patient_${index}`]?.rating || ''}
                                      onChange={(e) => handleTrainingDataChange(`patient_${index}`, 'rating', e.target.value)}
                                    >
                                      <option value="excellent">Excellent</option>
                                      <option value="good">Good</option>
                                      <option value="fair">Fair</option>
                                      <option value="poor">Poor</option>
                                    </Select>
                                  </FormControl>
                                  <FormControl size="sm">
                                    <FormLabel fontSize="xs">Comments</FormLabel>
                                    <Textarea
                                      placeholder="Training comments..."
                                      size="sm"
                                      rows={2}
                                      value={trainingData[`patient_${index}`]?.comments || ''}
                                      onChange={(e) => handleTrainingDataChange(`patient_${index}`, 'comments', e.target.value)}
                                    />
                                  </FormControl>
                                </VStack>
                              )}
                            </VStack>
                          </CardBody>
                        </Card>
                      ))}
                    </SimpleGrid>
                  ) : (
                    <Alert status="info">
                      <AlertIcon />
                      No patients found in the document
                    </Alert>
                  )}
                </CardBody>
              </Collapse>
            </Card>

            {/* AI Evaluation Results */}
            {aiEvaluation && (
              <Card>
                <CardHeader>
                  <HStack justify="space-between">
                    <HStack>
                      <Heading size="md">AI Evaluation</Heading>
                      <Badge colorScheme="purple">AI Generated</Badge>
                    </HStack>
                    <HStack>
                      <Tooltip label="Download AI Evaluation">
                        <IconButton
                          aria-label="Download AI evaluation"
                          icon={<DownloadIcon />}
                          size="sm"
                          onClick={() => downloadJson(aiEvaluation, `ai-evaluation-${Date.now()}.json`)}
                        />
                      </Tooltip>
                      <IconButton
                        aria-label="Toggle AI evaluation"
                        icon={isAiEvaluationOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        size="sm"
                        onClick={toggleAiEvaluation}
                      />
                    </HStack>
                  </HStack>
                </CardHeader>
                <Collapse in={isAiEvaluationOpen}>
                  <CardBody pt={0}>
                    <VStack align="start" spacing={4}>
                      {aiEvaluation.summary && (
                        <Box>
                          <Text fontWeight="bold" mb={2}>Summary:</Text>
                          <Text fontSize="sm">{aiEvaluation.summary}</Text>
                        </Box>
                      )}
                      
                      {aiEvaluation.scores && (
                        <Box>
                          <Text fontWeight="bold" mb={2}>Scores:</Text>
                          <SimpleGrid columns={2} spacing={2}>
                            {Object.entries(aiEvaluation.scores).map(([key, value]) => (
                              <HStack key={key} justify="space-between">
                                <Text fontSize="sm">{key.replace(/_/g, ' ').toUpperCase()}:</Text>
                                <Badge colorScheme={value >= 0.8 ? 'green' : value >= 0.6 ? 'yellow' : 'red'}>
                                  {(value * 100).toFixed(0)}%
                                </Badge>
                              </HStack>
                            ))}
                          </SimpleGrid>
                        </Box>
                      )}

                      {aiEvaluation.recommendations && (
                        <Box>
                          <Text fontWeight="bold" mb={2}>Recommendations:</Text>
                          <VStack align="start" spacing={1}>
                            {aiEvaluation.recommendations.map((rec, index) => (
                              <Text key={index} fontSize="sm">• {rec}</Text>
                            ))}
                          </VStack>
                        </Box>
                      )}
                    </VStack>
                  </CardBody>
                </Collapse>
              </Card>
            )}

            {/* Raw Data Section */}
            <Card>
              <CardHeader>
                <HStack justify="space-between">
                  <Heading size="md">Raw Data</Heading>
                  <HStack>
                    <Tooltip label="Download Extracted Data">
                      <IconButton
                        aria-label="Download extracted data"
                        icon={<DownloadIcon />}
                        size="sm"
                        onClick={() => downloadJson(extractedData, `extracted-data-${Date.now()}.json`)}
                      />
                    </Tooltip>
                    <IconButton
                      aria-label="Toggle raw data"
                      icon={isRawDataOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      size="sm"
                      onClick={toggleRawData}
                    />
                  </HStack>
                </HStack>
              </CardHeader>
              <Collapse in={isRawDataOpen}>
                <CardBody pt={0}>
                  <Box
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    overflow="auto"
                    maxH="500px"
                  >
                    <SyntaxHighlighter
                      language="json"
                      style={tomorrow}
                      customStyle={{
                        margin: 0,
                        fontSize: '12px',
                      }}
                    >
                      {JSON.stringify(extractedData, null, 2)}
                    </SyntaxHighlighter>
                  </Box>
                </CardBody>
              </Collapse>
            </Card>
          </>
        )}
      </VStack>
    </Box>
  );
}